import React, { useState } from 'react';
import axios from 'axios';
import Footer from './Footer';

const ROLES = ['Journalist', 'Photographer', 'Videographer', 'Other'];

const inputClass = "w-full border border-gray-300 dark:border-white/15 p-3 rounded-lg bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-gray-900 dark:focus:border-white transition-colors";

const MediaRegistration = () => {
  const [fullName, setFullName] = useState('');
  const [outlet, setOutlet] = useState('');
  const [role, setRole] = useState('Journalist');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');
    setSubmitting(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/api/media-registrations/upload`, {
        fullName,
        outlet,
        role,
        phone,
        email,
        agreementAccepted,
      });

      if (response.status === 200) {
        setSuccessMessage('Thank you — your media accreditation request has been submitted for review.');
        setProgress(100);

        setFullName('');
        setOutlet('');
        setRole('Journalist');
        setPhone('');
        setEmail('');
        setAgreementAccepted(false);

        setTimeout(() => {
          setProgress(0);
          setSuccessMessage('');
        }, 10000);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to submit. Please try again.');
      setProgress(100);
      setTimeout(() => {
        setProgress(0);
        setErrorMessage('');
      }, 5000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-black transition-colors duration-300">
      <div className="flex flex-col justify-center items-center gap-6 px-4 pt-[120px] pb-16 min-h-screen max-w-4xl mx-auto">
        <div className="bg-white dark:bg-[#111010] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white p-6 sm:p-8 rounded-xl w-full max-w-lg">
          <h2 className="text-2xl sm:text-3xl text-center mb-3 font-glsnecb tracking-wide uppercase">
            Media Accreditation
          </h2>
          <p className="text-center text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-6">
            Register here if you're covering the None Urabikoze Worship Concert as press, photo or video media.
            Your request will be reviewed before accreditation is confirmed.
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
            <input
              type="text"
              placeholder="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className={inputClass}
            />
            <input
              type="text"
              placeholder="Media outlet / organization (optional)"
              value={outlet}
              onChange={(e) => setOutlet(e.target.value)}
              className={inputClass}
            />
            <select value={role} onChange={(e) => setRole(e.target.value)} className={inputClass}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input
              type="tel"
              placeholder="Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              className={inputClass}
            />
            <input
              type="email"
              placeholder="Email Address (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />

            <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg p-3 leading-relaxed">
              By registering, you agree to follow event staff instructions, stay within designated media areas,
              and respect attendees' privacy while covering the event. Accreditation may be withdrawn for
              non-compliance.
            </div>
            <label className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={agreementAccepted}
                onChange={(e) => setAgreementAccepted(e.target.checked)}
                required
                className="mt-0.5"
              />
              I agree to the event media guidelines above.
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-gray-900 text-white dark:bg-white dark:text-black text-sm font-medium py-3 px-4 rounded-lg hover:bg-black dark:hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </form>

          <p className="text-center text-gray-400 dark:text-gray-500 text-xs mt-6">
            Questions? Contact: <a href="mailto:info@elayonemusic.org" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">info@elayonemusic.org</a>
          </p>
        </div>
      </div>

      {successMessage && (
        <div className="fixed top-6 left-4 right-4 sm:mx-auto sm:w-80 bg-green-100 text-green-900 border-green-500 pt-2 rounded-t-md shadow-md z-50">
          <div className="flex justify-center items-center">
            <span className="text-center p-1">{successMessage}</span>
          </div>
          <div className="h-1 mt-1 bg-green-300">
            <div style={{ width: `${progress}%` }} className="h-full bg-green-700 transition-all duration-500" />
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="fixed top-6 left-4 right-4 sm:mx-auto sm:w-80 bg-red-100 text-red-900 border-red-500 pt-2 rounded-t-md shadow-md z-50">
          <div className="flex justify-center items-center">
            <span className="text-center p-1">{errorMessage}</span>
          </div>
          <div className="h-1 mt-1 bg-red-300">
            <div style={{ width: `${progress}%` }} className="h-full bg-red-700 transition-all duration-500" />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MediaRegistration;
